import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  ExternalLink,
  FileText,
  Quote,
  Clock,
  Briefcase,
  Sparkle,
} from "lucide-react";
import { resolveImage } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePublicData } from "../hooks/usePublicData";

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { site, projects } = usePublicData();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/by-slug/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("not found"))))
      .then((d) => alive && setProject(d))
      .catch(() => alive && setProject(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center" style={{ backgroundColor: "var(--bg)" }}>
        <div className="text-sm text-[color:var(--muted)]">Loading…</div>
      </div>
    );
  if (!project)
    return (
      <div
        className="min-h-screen grid place-items-center px-6 text-center"
        style={{ backgroundColor: "var(--bg)" }}
      >
        <div>
          <div className="font-display text-3xl mb-3">Project not found</div>
          <button onClick={() => navigate("/")} className="mk-btn mk-btn-violet">
            <ArrowLeft className="w-4 h-4" /> Back home
          </button>
        </div>
      </div>
    );

  const p = project;
  const blocks = p.content_blocks || [];
  const related = (projects || [])
    .filter((x) => x.id !== p.id && x.category === p.category)
    .slice(0, 3);

  return (
    <div style={{ backgroundColor: "var(--bg)" }} data-testid="project-detail">
      <Header site={site} />

      {/* Cinematic cover hero */}
      <section
        className="relative w-full h-[70vh] md:h-[85vh] overflow-hidden"
        style={{ backgroundColor: p.color || "#1a1a1a" }}
      >
        {p.cover && (
          <img src={resolveImage(p.cover)} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
        <Link
          to="/#work"
          data-testid="back-to-work"
          className="absolute top-6 left-6 md:top-8 md:left-8 inline-flex items-center gap-2 text-sm text-white/85 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to work
        </Link>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-16 text-white">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2 text-xs mb-4 opacity-90">
              {p.tag && (
                <span className="px-3 py-1 rounded-full bg-[color:var(--violet)] text-white font-medium">
                  {p.tag}
                </span>
              )}
              {p.year && (
                <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30 backdrop-blur">
                  {p.year}
                </span>
              )}
              {p.client && <span className="text-white/80">— {p.client}</span>}
            </div>
            <h1 data-testid="project-title" className="font-display italic font-semibold text-4xl md:text-7xl leading-[0.98]">
              {p.title}
            </h1>
            {(p.description || p.summary) && (
              <p className="mt-4 max-w-2xl text-base md:text-xl text-white/85 leading-relaxed">
                {p.description || p.summary}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Overview strip */}
      <section className="relative bg-[color:var(--bg)] border-b border-[color:var(--card-border)]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <InfoBox icon={<Briefcase className="w-4 h-4" />} label="Role" value={p.role} />
          <InfoBox icon={<Clock className="w-4 h-4" />} label="Timeline" value={p.duration} />
          <InfoBox
            icon={<Sparkle className="w-4 h-4" />}
            label="Deliverables"
            value={p.overview?.deliverables}
          />
          <InfoBox label="Tools" value={(p.tools || []).join(", ")} />
        </div>
      </section>

      {/* The Challenge */}
      {(p.creative_brief || p.brief) && (
        <StorySection
          eyebrow="The Challenge"
          heading={p.challenge_heading || "What we needed to solve"}
          body={p.creative_brief || p.brief}
          image={p.challenge_image}
          layout={p.challenge_layout || "image-right"}
          bg="var(--bg-2)"
        />
      )}

      {/* Research & Discovery */}
      {p.research_body && (
        <StorySection
          eyebrow="Research &amp; Discovery"
          heading={p.research_heading || "Digging into the problem"}
          body={p.research_body}
          image={p.research_image}
          layout={p.research_layout || "image-left"}
        />
      )}

      {/* Design Process — numbered steps with optional images */}
      {(p.design_process || []).length > 0 && (
        <section className="relative py-16 md:py-24 bg-[color:var(--bg-2)]">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)]">
              Design Process
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-5xl font-semibold leading-tight">
              How it came together.
            </h2>
            <div className="mt-10 space-y-10 md:space-y-14">
              {(p.design_process || []).map((step, i) => {
                const imgOnLeft = i % 2 === 1; // alternate: 0 text-L img-R, 1 img-L text-R, etc.
                return (
                  <div key={i} className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
                    <div className={`md:col-span-5 ${imgOnLeft ? "md:order-2" : "md:order-1"}`}>
                      <div className="font-display text-6xl md:text-7xl font-semibold text-[color:var(--violet)] leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold">
                        {step.title}
                      </h3>
                      {step.body && (
                        <p className="mt-3 text-[color:var(--ink-soft)] leading-relaxed whitespace-pre-wrap">
                          {step.body}
                        </p>
                      )}
                    </div>
                    <div className={`md:col-span-7 ${imgOnLeft ? "md:order-1" : "md:order-2"}`}>
                      {step.image && (
                        <img
                          src={resolveImage(step.image)}
                          alt={step.title}
                          className="w-full rounded-2xl object-cover aspect-[4/3] border border-[color:var(--card-border)]"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Concept exploration */}
      {(p.concepts || []).length > 0 && (
        <ImageGallery
          eyebrow="Concept Exploration"
          heading="Exploring directions"
          items={p.concepts}
          columns={2}
        />
      )}

      {/* Final designs / mockups */}
      {(p.final_deliverables || []).length > 0 && (
        <ImageGallery
          eyebrow="Final Design"
          heading="The delivered work"
          items={p.final_deliverables}
          columns={3}
        />
      )}

      {(p.mockups || []).length > 0 && (
        <ImageGallery
          eyebrow="In Context"
          heading="Seeing it in the wild"
          items={p.mockups}
          columns={2}
          bg="var(--bg-2)"
        />
      )}

      {/* Simple gallery fallback */}
      {(p.gallery || []).filter(Boolean).length > 0 && (p.final_deliverables || []).length === 0 && (
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {p.gallery.filter(Boolean).map((src, i) => (
              <img
                key={i}
                src={resolveImage(src)}
                alt=""
                className="w-full rounded-xl aspect-[4/3] object-cover"
              />
            ))}
          </div>
        </section>
      )}

      {/* Outcome / Impact */}
      {((p.results_v2 || []).length > 0 || (p.results || []).length > 0 || p.outcome_body) && (
        <section className="relative py-16 md:py-24 bg-[color:var(--bg)]">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)]">
              Outcome &amp; Impact
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-5xl font-semibold leading-tight">
              {p.outcome_heading || "What it delivered."}
            </h2>
            {p.outcome_body && (
              <p className="mt-5 max-w-3xl text-[color:var(--ink-soft)] leading-relaxed whitespace-pre-wrap">
                {p.outcome_body}
              </p>
            )}
            {(p.results_v2 || []).length > 0 && (
              <div className="mt-10 grid md:grid-cols-3 gap-5">
                {p.results_v2.map((r, i) => (
                  <div
                    key={i}
                    className="mk-card p-6 rounded-2xl border border-[color:var(--card-border)] bg-[color:var(--card)]"
                  >
                    <div className="font-display text-4xl md:text-5xl font-semibold text-[color:var(--violet)]">
                      {r.value}
                    </div>
                    <div className="mt-2 font-display text-lg font-semibold">{r.title}</div>
                    {r.description && (
                      <p className="mt-1.5 text-sm text-[color:var(--ink-soft)]">{r.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(p.results || []).length > 0 && (p.results_v2 || []).length === 0 && (
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
                {p.results.map((r, i) => (
                  <div key={i} className="rounded-2xl p-5 bg-[color:var(--bg-2)] border border-[color:var(--card-border)]">
                    <div className="font-display text-3xl font-semibold text-[color:var(--violet)]">
                      {r.value}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mt-2">
                      {r.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Testimonial */}
      {p.testimonial && (
        <section className="py-16 md:py-24 bg-[color:var(--ink)] text-[color:var(--bg)]">
          <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
            <Quote className="w-8 h-8 text-[color:var(--violet)] mx-auto" />
            <blockquote className="mt-4 font-display italic text-2xl md:text-4xl leading-snug">
              &ldquo;{p.testimonial}&rdquo;
            </blockquote>
            {p.testimonial_by && (
              <div className="mt-5 text-sm text-[color:var(--bg)]/70">— {p.testimonial_by}</div>
            )}
          </div>
        </section>
      )}

      {/* Free-form content blocks (still supported) */}
      {blocks.length > 0 && (
        <div className="relative">
          {blocks.map((b, i) => (
            <ContentBlock key={i} block={b} />
          ))}
        </div>
      )}

      {/* Optional: final "extras" gallery — a few last random images */}
      {(p.final_gallery || []).filter(Boolean).length > 0 && (
        <section className="py-14 md:py-20 bg-[color:var(--bg-2)]">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[color:var(--muted)]">
              A few more
            </div>
            <h2 className="mt-3 font-display italic text-3xl md:text-5xl font-semibold leading-tight">
              Extras &amp; process shots.
            </h2>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {p.final_gallery.filter(Boolean).map((src, i) => (
                <img
                  key={i}
                  src={resolveImage(src)}
                  alt=""
                  className="w-full rounded-xl aspect-[4/3] object-cover"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PDFs */}
      {(p.pdfs || []).length > 0 && (
        <section className="w-full py-14 md:py-20 bg-[color:var(--bg-2)]">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <h2 className="font-display italic font-semibold text-2xl md:text-3xl mb-6">Documents</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {p.pdfs.map((d, i) => (
                <a
                  key={i}
                  href={resolveImage(d.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 p-4 rounded-xl bg-[color:var(--card)] border border-[color:var(--card-border)] hover:border-[color:var(--ink)] transition-colors"
                >
                  <FileText className="w-5 h-5 text-[color:var(--violet)]" />
                  <span className="text-sm font-medium truncate flex-1">
                    {d.name || `Document ${i + 1}`}
                  </span>
                  <Download className="w-4 h-4 text-[color:var(--muted)]" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="py-14 md:py-20 border-t border-[color:var(--card-border)]">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display italic text-2xl md:text-3xl font-semibold">
                More like this
              </h2>
              <Link to="/#work" className="text-sm mk-link">
                All projects <ArrowRight className="inline w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/work/${r.slug}`}
                  className="group relative block rounded-xl overflow-hidden aspect-[4/3]"
                  style={{ backgroundColor: r.color }}
                >
                  {r.cover && (
                    <img
                      src={resolveImage(r.cover)}
                      alt={r.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white font-display italic font-semibold text-sm truncate">
                    {r.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer site={site} />
    </div>
  );
};

const InfoBox = ({ icon, label, value }) => {
  if (!value) return <div />;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)]">
        {icon} {label}
      </div>
      <div className="mt-1.5 text-sm md:text-base font-medium">{value}</div>
    </div>
  );
};

const StorySection = ({ eyebrow, heading, body, image, bg, layout = "image-right" }) => {
  const bgStyle = bg ? { backgroundColor: bg } : {};

  // Centered text with a big landscape image below.
  if (layout === "image-below" || layout === "center") {
    return (
      <section className="relative py-16 md:py-24" style={bgStyle}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <div
            className="text-[11px] tracking-[0.24em] uppercase text-[color:var(--muted)]"
            dangerouslySetInnerHTML={{ __html: eyebrow }}
          />
          <h2 className="mt-3 font-display italic text-3xl md:text-5xl font-semibold leading-tight">
            {heading}
          </h2>
          {body && (
            <p className="mt-6 mx-auto max-w-3xl text-[color:var(--ink-soft)] leading-relaxed whitespace-pre-wrap text-base md:text-lg">
              {body}
            </p>
          )}
          {image && (
            <div className="mt-10 md:mt-12">
              <img
                src={resolveImage(image)}
                alt={heading}
                className="w-full rounded-2xl object-cover aspect-[21/9] border border-[color:var(--card-border)]"
              />
            </div>
          )}
        </div>
      </section>
    );
  }

  // Side-by-side (default). Image can be left or right.
  const imgFirst = layout === "image-left";
  return (
    <section className="relative py-16 md:py-24" style={bgStyle}>
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
        <div className={`md:col-span-6 ${imgFirst ? "md:order-2" : "md:order-1"}`}>
          <div
            className="text-[11px] tracking-[0.24em] uppercase text-[color:var(--muted)]"
            dangerouslySetInnerHTML={{ __html: eyebrow }}
          />
          <h2 className="mt-3 font-display italic text-3xl md:text-5xl font-semibold leading-tight">
            {heading}
          </h2>
          {body && (
            <p className="mt-5 text-[color:var(--ink-soft)] leading-relaxed whitespace-pre-wrap">
              {body}
            </p>
          )}
        </div>
        <div className={`md:col-span-6 ${imgFirst ? "md:order-1" : "md:order-2"}`}>
          {image && (
            <img
              src={resolveImage(image)}
              alt={heading}
              className="w-full rounded-2xl object-cover aspect-[4/3] border border-[color:var(--card-border)]"
            />
          )}
        </div>
      </div>
    </section>
  );
};

const ImageGallery = ({ eyebrow, heading, items = [], columns = 2, bg }) => (
  <section className="relative py-16 md:py-20" style={bg ? { backgroundColor: bg } : {}}>
    <div className="max-w-6xl mx-auto px-5 md:px-8">
      <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)]">{eyebrow}</div>
      <h2 className="mt-3 font-display italic text-3xl md:text-5xl font-semibold leading-tight">
        {heading}
      </h2>
      <div
        className={`mt-8 grid gap-4 md:gap-6 ${
          columns === 3 ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"
        }`}
      >
        {items.map((it, i) => (
          <figure key={i} className="space-y-2">
            {it.image && (
              <img
                src={resolveImage(it.image)}
                alt={it.caption || ""}
                className="w-full rounded-2xl object-cover aspect-[4/3] border border-[color:var(--card-border)]"
              />
            )}
            {it.caption && (
              <figcaption className="text-sm italic text-[color:var(--muted)] px-1">
                {it.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </div>
  </section>
);

// Legacy content blocks (masks removed)
const ContentBlock = ({ block }) => {
  const t = block.type;
  const bg = block.bg_color;
  const wrapStyle = bg ? { backgroundColor: bg } : {};

  if (t === "image_full") {
    return (
      <figure className="relative w-full py-6">
        {block.image && (
          <img src={resolveImage(block.image)} alt={block.caption || ""} className="w-full h-auto block" />
        )}
        {block.caption && (
          <figcaption className="max-w-6xl mx-auto px-5 md:px-8 py-3 text-sm italic text-[color:var(--muted)] text-center">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }
  if (t === "text_over_image") {
    const align = block.text_position || "center";
    return (
      <section className="relative w-full min-h-[60vh] md:min-h-[70vh] flex items-center overflow-hidden">
        {block.image && (
          <img src={resolveImage(block.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div
          className={`relative w-full max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 text-white ${
            align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center"
          }`}
        >
          {block.eyebrow && (
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/75 mb-3">
              {block.eyebrow}
            </div>
          )}
          {block.heading && (
            <h2 className="font-display italic font-semibold text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
              {block.heading}
            </h2>
          )}
          {block.body && (
            <p className="mt-5 text-base md:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed whitespace-pre-wrap">
              {block.body}
            </p>
          )}
        </div>
      </section>
    );
  }
  if (t === "image_left" || t === "image_right") {
    const imgLeft = t === "image_left";
    return (
      <section className="relative w-full py-14 md:py-20" style={wrapStyle}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-8 md:gap-14 items-center">
          {imgLeft && block.image && (
            <img src={resolveImage(block.image)} alt="" className="w-full rounded-2xl aspect-[4/3] object-cover" />
          )}
          <div>
            {block.eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-3">
                {block.eyebrow}
              </div>
            )}
            {block.heading && (
              <h2 className="font-display italic font-semibold text-3xl md:text-4xl leading-tight">
                {block.heading}
              </h2>
            )}
            {block.body && (
              <p className="mt-4 text-[color:var(--ink-soft)] leading-relaxed whitespace-pre-wrap">
                {block.body}
              </p>
            )}
          </div>
          {!imgLeft && block.image && (
            <img src={resolveImage(block.image)} alt="" className="w-full rounded-2xl aspect-[4/3] object-cover" />
          )}
        </div>
      </section>
    );
  }
  if (t === "text_only") {
    return (
      <section className="relative w-full py-14 md:py-20" style={wrapStyle}>
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          {block.eyebrow && (
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-3">
              {block.eyebrow}
            </div>
          )}
          {block.heading && (
            <h2 className="font-display italic font-semibold text-3xl md:text-5xl leading-tight">
              {block.heading}
            </h2>
          )}
          {block.body && (
            <p className="mt-5 text-lg text-[color:var(--ink-soft)] leading-relaxed whitespace-pre-wrap">
              {block.body}
            </p>
          )}
        </div>
      </section>
    );
  }
  if (t === "gallery") {
    const imgs = block.images || [];
    return (
      <section className="w-full py-14 md:py-20" style={wrapStyle}>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {block.heading && (
            <h2 className="font-display italic font-semibold text-2xl md:text-3xl mb-6">
              {block.heading}
            </h2>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {imgs.map((src, i) => (
              <img key={i} src={resolveImage(src)} alt="" className="w-full rounded-xl aspect-[4/3] object-cover" />
            ))}
          </div>
        </div>
      </section>
    );
  }
  if (t === "quote") {
    return (
      <section className="w-full bg-[color:var(--ink)] text-[color:var(--bg)]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <Quote className="w-8 h-8 text-[color:var(--violet)] mx-auto" />
          <blockquote className="mt-4 font-display italic text-2xl md:text-4xl leading-snug">
            &ldquo;{block.quote}&rdquo;
          </blockquote>
          {block.author && <div className="mt-5 text-sm text-[color:var(--bg)]/70">— {block.author}</div>}
        </div>
      </section>
    );
  }
  if (t === "pdf") {
    const pdfs = block.pdfs || [];
    if (pdfs.length === 0) return null;
    return (
      <section className="w-full py-14 md:py-20 bg-[color:var(--bg-2)]">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          {block.heading && (
            <h2 className="font-display italic font-semibold text-2xl md:text-3xl mb-6">
              {block.heading}
            </h2>
          )}
          <div className="rounded-xl overflow-hidden border border-[color:var(--card-border)] bg-white">
            <iframe
              src={`${resolveImage(pdfs[0].url)}#toolbar=1&view=FitH`}
              title={pdfs[0].name}
              className="w-full h-[480px] md:h-[640px]"
            />
            <div className="p-4 flex items-center gap-3 border-t border-[color:var(--card-border)] bg-[color:var(--card)]">
              <FileText className="w-4 h-4 text-[color:var(--violet)]" />
              <span className="text-sm font-medium truncate">{pdfs[0].name || "Document"}</span>
              <a
                href={resolveImage(pdfs[0].url)}
                target="_blank"
                rel="noreferrer"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[color:var(--card-border)] text-xs"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            </div>
          </div>
          {pdfs.length > 1 && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {pdfs.slice(1).map((d, i) => (
                <a
                  key={i}
                  href={resolveImage(d.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 p-4 rounded-xl bg-[color:var(--card)] border border-[color:var(--card-border)] hover:border-[color:var(--ink)] transition-colors"
                >
                  <FileText className="w-5 h-5 text-[color:var(--violet)]" />
                  <span className="text-sm font-medium truncate flex-1">
                    {d.name || `Document ${i + 2}`}
                  </span>
                  <ExternalLink className="w-4 h-4 text-[color:var(--muted)]" />
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }
  return null;
};

export default ProjectDetail;
